import type {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

/**
 * Hook to manage infinite scrolling for Universal Chat messages
 *
 * This hook handles the logic of detecting when a user has scrolled
 * to a point where older messages should be loaded, and triggering the
 * loading of those messages.
 *
 * It integrates with react-intersection-observer to detect when the user
 * has scrolled to the load-more trigger element, and then calls the fetchNextPage
 * function provided by react-query's useInfiniteQuery.
 *
 * @param hasNextPage - Whether there are more pages to load
 * @param isFetchingNextPage - Whether the next page is currently being fetched
 * @param fetchNextPage - Function to fetch the next page of messages
 * @returns Object containing a ref to be attached to the load-more trigger element
 */
interface UseUniversalChatInfiniteScrollProps {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions,
  ) => Promise<InfiniteQueryObserverResult>;
}

export function useUniversalChatInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseUniversalChatInfiniteScrollProps) {
  // Create an intersection observer to detect when the load-more element is visible
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
  });

  // Fetch the next page of messages when the load-more element comes into view
  // and there are more messages to load
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    loadMoreRef,
  };
}
